import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  HelpOutline as HelpIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
const RESULT_CONFIG = {
  correct: { label: "Correct", color: "success", bgcolor: "#dcfce7", textColor: "#166534" },
  partial: { label: "Partial", color: "warning", bgcolor: "#fef3c7", textColor: "#92400e" },
  incorrect: { label: "Incorrect", color: "error", bgcolor: "#fee2e2", textColor: "#991b1b" },
  written: { label: "Written", color: "default", bgcolor: "#f3f4f6", textColor: "#374151" },
  unanswered: { label: "Unanswered", color: "default", bgcolor: "#f3f4f6", textColor: "#6b7280" },
};

const fmt = (n) => {
  const num = Number(n ?? 0);
  // Up to 2 decimals (fixed-total group parts are fractions like 5/3 ≈ 1.67), trailing zeros dropped.
  return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
};

const LATEX_SYMBOLS = [
  [/\\times/g, "×"], [/\\div/g, "÷"], [/\\pm/g, "±"], [/\\mp/g, "∓"],
  [/\\cdot/g, "·"], [/\\leq?\b/g, "≤"], [/\\geq?\b/g, "≥"], [/\\neq/g, "≠"],
  [/\\approx/g, "≈"], [/\\equiv/g, "≡"], [/\\infty/g, "∞"], [/\\pi\b/g, "π"],
  [/\\degree|\\circ/g, "°"], [/\\(?:ldots|cdots|dots)/g, "…"],
  [/\\rightarrow|\\to\b/g, "→"], [/\\percent|\\%/g, "%"],
];

/**
 * Spreadsheet cells can't typeset, so the export has to carry readable text rather than source.
 * Without this a stem went into the file as "i.e. $2 \times 2 \times 3 \times 3 = 36$".
 */
const latexToPlainText = (text) => {
  let out = text
    .replace(/\$\$([\s\S]*?)\$\$/g, "$1")
    .replace(/\\\[([\s\S]*?)\\\]/g, "$1")
    .replace(/\\\(([\s\S]*?)\\\)/g, "$1")
    // A lone "$" left after the pairs are gone is a price, so only unwrap balanced spans.
    .replace(/\$([^$]+)\$/g, "$1");

  // Nested fractions resolve inside-out; three passes covers anything a stem realistically holds.
  for (let i = 0; i < 3; i++) {
    out = out.replace(/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, "$1/$2");
  }
  out = out
    .replace(/\\sqrt\s*\{([^{}]*)\}/g, "√($1)")
    .replace(/\\(?:text|mathrm|mathbf|operatorname)\s*\{([^{}]*)\}/g, "$1");
  LATEX_SYMBOLS.forEach(([re, ch]) => { out = out.replace(re, ch); });

  return out
    .replace(/\\(?:quad|qquad|,|;|!|\s)/g, " ")
    .replace(/\^\{([^{}]*)\}/g, "^$1")
    .replace(/_\{([^{}]*)\}/g, "_$1")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const htmlToText = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
  return latexToPlainText(text);
};

const ResultChip = ({ result }) => {
  const cfg = RESULT_CONFIG[result] ?? RESULT_CONFIG.written;
  const icon =
    result === "correct" ? <CheckIcon sx={{ fontSize: 13 }} /> :
    result === "incorrect" ? <WrongIcon sx={{ fontSize: 13 }} /> :
    result === "written" || result === "unanswered" ? <HelpIcon sx={{ fontSize: 13 }} /> : undefined;
  return (
    <Chip
      icon={icon}
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22, bgcolor: cfg.bgcolor, color: cfg.textColor }}
    />
  );
};

const MarksBreakdownTable = ({ rows = [], totalMarks = 0, scoredMarks = 0, title = "Marks Breakdown", fileName = "marks-breakdown" }) => {
  const [downloading, setDownloading] = useState(false);

  if (!rows.length) return null;

  const earnedTotal = rows.reduce((s, r) => s + Number(r.earned ?? 0), 0);
  const maxTotal = totalMarks || rows.reduce((s, r) => s + Number(r.max ?? 0), 0);
  // The footer must never contradict the rows above it. Taking scoredMarks verbatim showed a total of
  // "0.0" over a table whose own rows read "Correct — 1 mark" (an AI score of 0 had overridden the
  // deterministic score). The passed-in total still wins when it is HIGHER — it can include credit the
  // per-row view doesn't model — but it can never report less than the rows visibly add up to.
  const displayEarned = scoredMarks != null
    ? Math.max(Number(scoredMarks), earnedTotal)
    : earnedTotal;
  const lostOf = (r) => Math.max(0, Number(r.max ?? 0) - Number(r.earned ?? 0));
  const lostTotal = Math.max(0, maxTotal - displayEarned);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Marks Breakdown");
      ws.columns = [
        { header: "Q", key: "q", width: 8 },
        { header: "Part", key: "part", width: 8 },
        { header: "Question", key: "question", width: 60 },
        { header: "Result", key: "result", width: 14 },
        { header: "Marks Earned", key: "earned", width: 14 },
        { header: "Marks Lost", key: "lost", width: 12 },
        { header: "Max Marks", key: "max", width: 12 },
      ];
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4338CA" } };
      headerRow.alignment = { vertical: "middle" };

      rows.forEach((r) => {
        ws.addRow({
          q: r.qLabel ?? "",
          part: r.partLabel ?? "",
          question: htmlToText(r.questionText),
          result: (RESULT_CONFIG[r.result] ?? RESULT_CONFIG.written).label,
          earned: Number(r.earned ?? 0),
          lost: Number(lostOf(r).toFixed(2)),
          max: Number(r.max ?? 0),
        });
      });

      const totalRow = ws.addRow({
        q: "",
        part: "",
        question: "TOTAL",
        result: "",
        earned: Number(displayEarned.toFixed(2)),
        lost: Number(lostTotal.toFixed(2)),
        max: Number(maxTotal),
      });
      totalRow.font = { bold: true };
      totalRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEDE9FE" } };

      ws.getColumn("question").alignment = { wrapText: true, vertical: "top" };
      ws.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
            disabled={downloading}
            onClick={handleDownload}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Download Excel
          </Button>
        </Box>
        <TableContainer sx={{ maxHeight: 520, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f5f3ff", whiteSpace: "nowrap" } }}>
                <TableCell>Q</TableCell>
                <TableCell>Part</TableCell>
                <TableCell>Result</TableCell>
                <TableCell align="right">Marks Earned</TableCell>
                <TableCell align="right">Marks Lost</TableCell>
                <TableCell align="right">Max</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.key ?? i} sx={{ bgcolor: i % 2 === 1 ? "#fafafa" : "transparent" }}>
                  <TableCell sx={{ fontWeight: r.isMainRow ? 700 : 400, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {r.qLabel ?? ""}
                  </TableCell>
                  <TableCell sx={{ pl: r.partLabel ? 2 : 1.5 }}>
                    {r.partLabel ? (
                      <Chip label={r.partLabel} size="small" variant="outlined" color="primary"
                        sx={{ fontWeight: 700, fontSize: "0.66rem", height: 20, minWidth: 26 }} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell><ResultChip result={r.result} /></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#166534", whiteSpace: "nowrap" }}>{fmt(r.earned)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: lostOf(r) > 0 ? "#991b1b" : "text.disabled", whiteSpace: "nowrap" }}>{fmt(lostOf(r))}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{fmt(r.max)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { bgcolor: "#ede9fe", fontWeight: 800, borderTop: "2px solid", borderColor: "#c4b5fd" } }}>
                <TableCell colSpan={3} sx={{ textAlign: "right" }}>TOTAL</TableCell>
                <TableCell align="right">{fmt(displayEarned)}</TableCell>
                <TableCell align="right">{fmt(lostTotal)}</TableCell>
                <TableCell align="right">{fmt(maxTotal)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default MarksBreakdownTable;
